import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  Paper,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha, styled, useTheme } from '@mui/material/styles';
import { collection, getDocs, getFirestore, limit, orderBy, query, where } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { InstagramEmbed } from 'react-social-media-embed';
import { useConsent } from '../context/ConsentContext';
import Carousel from 'react-material-ui-carousel';

// Images you already have
import heroImage3 from '../assets/Home/HomeImage1.jpeg';
import heroImage1 from '../assets/Home/HomeImage2.JPG';
import heroImage2 from '../assets/Home/HomeImage3.jpeg';


// Avatars
import avatar1 from '../assets/Home/avatar1.jpg';
import avatar2 from '../assets/Home/avatar2.jpg';
import avatar3 from '../assets/Home/avatar3.jpg';
import avatar4 from '../assets/Home/avatar4.jpg';
import avatar5 from '../assets/Home/avatar5.jpg';
import avatar6 from '../assets/Home/avatar6.png';
import avatar7 from '../assets/Home/avatar7.jpg';

// Partner Logos
import partnerLogo1 from '../assets/Home/Partner_EuropeDirect.png';
import partnerLogo2 from '../assets/Home/Partner_Federal_Ministry.png';
import partnerLogo3 from '../assets/Home/Partner_tpa.jpg';
import partnerLogo4 from '../assets/Home/Partner_Federal_Chancellery_Republic.png';

const HeroWrap = styled(Box)(({ theme }) => ({
  position: 'relative',
  borderRadius: 24,
  overflow: 'hidden',
  minHeight: 520,
  [theme.breakpoints.down('sm')]: {
    minHeight: 520,
    borderRadius: 18,
  },
  boxShadow: theme.shadows[3],
}));



const SubtleCard = styled(Card)(({ theme }) => ({
  borderRadius: 18,
  boxShadow: theme.shadows[1],
  border: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
  transition: 'transform .25s ease, box-shadow .25s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[4],
  },
}));

const PartnerLogo = styled('img')(({ theme }) => ({
  width: 150,
  height: 'auto',
  opacity: 0.95,
  transition: 'transform .25s ease, opacity .25s ease',
  '&:hover': {
    opacity: 1,
    transform: 'translateY(-2px) scale(1.02)',
  },
}));


const testimonials = [
  {
    name: 'Roberta Metsola',
    title: 'President of the European Parliament',
    text:
      'The European Youth Parliament is of remarkable value as a platform to foster political engagement among European youth.',
    avatar: avatar1,
  },
  {
    name: 'Asdin El Habbassi, BA',
    title: 'Member of the Austrian Parliament',
    text:
      'The participation of young people is indispensable in a strong Europe. Be a part of EYP — your future, your Europe!',
    avatar: avatar2,
  },
  {
    name: 'Mag. Maria Pichlbauer',
    title:
      'Counsellor for EU and Internationalisation at the local school authority in Styria',
    text:
      'EYP is an ideal instrument for pan-European political education that at the same time provides language training and skill development.',
    avatar: avatar3,
  },
  {
    name: 'Jochen Zöschg',
    title: 'Zurich Insurance Group',
    text:
      'Young, highly committed people in intercultural cooperation — politics, society and economy lived in a refreshing way.',
    avatar: avatar4,
  },
  {
    name: 'Robert Hietz',
    title: 'Student and member of EYP Austria',
    text:
      'EYP means new friends wherever you go and being challenged to become better — and discovering what being European means.',
    avatar: avatar5,
  },
  {
    name: 'Emily Usner',
    title: 'President of EYP Austria 2022/23',
    text:
      'The perfect combination of fun and learning, challenge and safety — a youth organisation for the future of Europe.',
    avatar: avatar6,
  },
  {
    name: 'Nico Janis',
    title: 'President of EYP Austria 2025/26',
    text:
      'Expression and exchange, friendship and responsibility — a place where young people can engage and shape the future.',
    avatar: avatar7,
  },
];


function useTypewriter(words, {
  typingSpeed = 85,
  deletingSpeed = 45,
  pauseMs = 1400,
} = {}) {
  const [wordIdx, setWordIdx] = useState(0);
  const [subIdx, setSubIdx] = useState(0);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const current = words[wordIdx] ?? "";
    const isDoneTyping = subIdx === current.length && !deleting;
    const isDoneDeleting = subIdx === 0 && deleting;

    let delay = deleting ? deletingSpeed : typingSpeed;
    if (isDoneTyping) delay = pauseMs;

    const t = setTimeout(() => {
      if (isDoneTyping) {
        setDeleting(true);
        return;
      }

      if (isDoneDeleting) {
        setDeleting(false);
        setWordIdx((i) => (i + 1) % words.length);
        return;
      }

      setSubIdx((i) => i + (deleting ? -1 : 1));
    }, delay);

    return () => clearTimeout(t);
  }, [words, wordIdx, subIdx, deleting, typingSpeed, deletingSpeed, pauseMs]);

  const text = (words[wordIdx] ?? "").slice(0, subIdx);
  return text;
}



const PhotoStack = ({ images = [] }) => {
  const theme = useTheme();
  const [offset, setOffset] = useState(0);

  // keeps order consistent: back -> middle -> top
  const ordered = useMemo(() => {
    if (!images.length) return [];
    return [0, 1, 2].map((i) => images[(i + offset) % images.length]);
  }, [images, offset]);


  const transforms = [
    "rotate(-9deg) translate(-9%, 7%)",
    "rotate(-4deg) translate(-0.5%, 0.5%)",
    "rotate(1deg) translate(4%, -1%)",
  ];

  const layerStyle = (layer) => ({
    transform: transforms[layer],
    zIndex: layer + 1,
    boxShadow: theme.shadows[7 + layer * 2],
    opacity: layer === 0 ? 0.92 : 1,
    filter: layer === 0 ? "saturate(0.95) contrast(0.98)" : "none",
  });

  const advance = () => {
    if (!images.length) return;
    setOffset((o) => (o + 1) % images.length);
  };

  return (
    <Box
      sx={{
        position: "relative",
        width: { xs: 320, sm: 380, md: 520 },
        height: { xs: 280, sm: 320, md: 420 },
        mx: { xs: "auto", md: 0 },
        cursor: "pointer",
        userSelect: "none",
      }}
      onClick={advance} // click anywhere on the stack
      role="button"
      aria-label="Rotate photo stack"
    >
      {/* soft static blob behind */}
      <Box
        sx={{
          position: "absolute",
          inset: "-14%",
          borderRadius: "50%",
          background: `radial-gradient(circle at 30% 30%, ${alpha(
            theme.palette.primary.main,
            0.18
          )} 0%, transparent 60%),
          radial-gradient(circle at 70% 60%, ${alpha(
            theme.palette.secondary?.main || theme.palette.primary.main,
            0.10
          )} 0%, transparent 55%)`,
          filter: "blur(14px)",
          zIndex: 0,
        }}
      />

      {ordered.slice(0, 3).map((src, layer) => (
        <Box
          key={`${src}-${layer}`}
          component="img"
          src={src}
          alt={`EYP stack ${layer + 1}`}
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            borderRadius: 4,
            border: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
            transition: "transform .35s ease, box-shadow .35s ease, filter .35s ease",
            ...layerStyle(layer),

            // small life on hover, but not too much
            "&:hover": {
              transform:
                layer === 2
                  ? "rotate(2deg) translate(4%, -2%) scale(1.01)"
                  : layer === 1
                  ? "rotate(-4deg) translate(0%, -6%) scale(1.01)"
                  : "rotate(-10deg) translate(-10%, 8%)",
              boxShadow: theme.shadows[14],
            },
          }}
        />
      ))}

      {/* label
      <Box
        sx={{
          position: "absolute",
          left: 16,
          bottom: 16,
          px: 1.4,
          py: 0.8,
          borderRadius: 999,
          backgroundColor: alpha(theme.palette.background.paper, 0.9),
          border: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
          backdropFilter: "blur(10px)",
          fontWeight: 900,
          fontSize: "0.95rem",
          zIndex: 5,
          pointerEvents: "none", // so click still rotates
        }}
      >
        #EYPAT
      </Box> */}
    </Box>
  );
};




const Hero = ({ onPrimary, onSecondary, images }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery("(max-width:900px)");

  const typeWords = useMemo(
    () => ["debate.", "community.", "Europe.", "opportunity.", "friendship."],
    []
  );
const typed = useTypewriter(typeWords, { typingSpeed: 85, deletingSpeed: 45, pauseMs: 1400 });

  return (
    <Box
      sx={{
        position: "relative",
        overflow: "hidden",
        py: { xs: 6, md: 9 },

        // full-width gentle wash so it doesn't feel like it ends at container edges
       
      }}
    >

      {/* soft background blobs (static, no moving animation) */}
      <Box
        sx={{
          position: "absolute",
          
        }}
      />
      <Box
        sx={{
          position: "absolute",
          
        }}
      />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1.05fr 0.95fr" },
            alignItems: "center",
            gap: { xs: 5, md: 6 },
          }}
        >
          {/* LEFT */}
          <Box>
            {/* Subtle title highlight (not boxed) */}
            <Box>
              <Typography
                variant={isMobile ? "h3" : "h1"}
                sx={{
                  fontWeight: 950,
                  letterSpacing: -1.2,
                  lineHeight: 0.95,
                }}
              >
                European
                <br />
                Youth
                <br />
                Parliament
                <br />
                Austria
              </Typography>

              <Box
                sx={{
                  mt: 1.2,
                  width: { xs: "65%", md: "60%" },
                  height: 8,
                  borderRadius: 999,
                  background: `linear-gradient(90deg,
                    ${alpha(theme.palette.primary.main, 0.28)} 0%,
                    ${alpha(theme.palette.primary.main, 0.06)} 100%)`,
                }}
              />
            </Box>


            {/* Prominent “We stand for …” */}
            <Typography
              sx={{
                mt: 2.5,
                fontSize: { xs: "1.15rem", md: "1.35rem" },
                fontWeight: 900,
                color: "text.primary",
              }}
            >
              We stand for{" "}
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  px: 1.2,
                  py: 0.25,
                  borderRadius: 999,
                  backgroundColor: alpha(theme.palette.primary.main, 0.10),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
                  fontWeight: 950,
                }}
              >
                {typed}
                <Box
                  component="span"
                  sx={{
                    display: "inline-block",
                    width: "0.6ch",
                    ml: 0.3,
                    animation: "blink 1s step-end infinite",
                    "@keyframes blink": {
                      "0%, 50%": { opacity: 1 },
                      "51%, 100%": { opacity: 0 },
                    },
                  }}
                >
                  |
                </Box>
              </Box>
            </Typography>

            <Typography
              sx={{
                mt: 1.2,
                color: "text.secondary",
                fontSize: { xs: "1.0rem", md: "1.08rem" },
                lineHeight: 1.7,
                maxWidth: 560,
              }}
            >
              A youth-led platform where young people engage with European politics, practise intercultural dialogue,
              and build leadership through sessions and events.
            </Typography>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 3 }}>
              <Button
                variant="contained"
                onClick={onPrimary}
                sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900, px: 3, py: 1.1 }}
              >
                Explore events & calls
              </Button>
              <Button
                variant="outlined"
                onClick={onSecondary}
                sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900, px: 3, py: 1.1 }}
              >
                Become a member
              </Button>
            </Stack>
          </Box>

          {/* RIGHT */}
          <Box sx={{ display: "flex", justifyContent: { xs: "center", md: "flex-end" } }}>
            <PhotoStack images={images} />
          </Box>
        </Box>
      </Container>
    </Box>
  );
};



const HomePage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width:600px)');
  const navigate = useNavigate();
  const { consent, openConsentDialog } = useConsent();

  const [news, setNews] = useState([]);
  const [newsLoading, setNewsLoading] = useState(true);

  const [nextEvent, setNextEvent] = useState(null);
  const [nextEventLoading, setNextEventLoading] = useState(true);

  useEffect(() => {
    const fetchNews = async () => {
    try {
      const db = getFirestore();
      const snap = await getDocs(collection(db, "news"));

      const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

      const toMillis = (t) => {
        if (!t) return 0;
        if (typeof t.toDate === "function") return t.toDate().getTime(); // Firestore Timestamp
        if (t instanceof Date) return t.getTime();
        if (typeof t === "number") return t;
        const parsed = Date.parse(t);
        return Number.isNaN(parsed) ? 0 : parsed;
      };

      const sorted = [...all].sort((a, b) => toMillis(b.timestamp) - toMillis(a.timestamp));
      setNews(sorted.slice(0, 3));
    } catch (e) {
      console.error("Failed to load news:", e);
      setNews([]);
    } finally {
      setNewsLoading(false);
    }
  };

    const fetchNextEvent = async () => {
      try {
        const db = getFirestore();
        const now = new Date();
        const q = query(
          collection(db, 'events'),
          where('startDate', '>', now),
          orderBy('startDate', 'asc'),
          limit(1)
        );
        const snap = await getDocs(q);
        setNextEvent(snap.docs.map((d) => ({ id: d.id, ...d.data() }))[0] || null);
      } catch (e) {
        console.error('Failed to load next event:', e);
      } finally {
        setNextEventLoading(false);
      }
    };

    fetchNews();
    fetchNextEvent();
  }, []);

  const stripHtmlToText = (html = "") => {
  if (!html) return "";
  const div = document.createElement("div");
  div.innerHTML = html;
  return (div.textContent || div.innerText || "").trim();
};

const excerpt = (html, max = 180) => {
  const txt = stripHtmlToText(html);
  return txt.length > max ? txt.slice(0, max).trim() + "…" : txt;
};


  return (
    <Box>
      <Container maxWidth={false} sx={{ mt: { xs: 4, md: 6 }, px: { xs: 2, md: 6 } }}>
  <Box sx={{ maxWidth: 1400, mx: "auto" }}>
    <Hero
      onPrimary={() => navigate("/events")}
      onSecondary={() => navigate("/membership")}
      images={[heroImage3, heroImage2, heroImage1]}
    />
  </Box>
</Container>

<Container maxWidth="lg" sx={{ mt: { xs: 4, md: 6 } }}>
  {/* VALUE PROPS ...rest of page... */}

        <Box sx={{ mt: { xs: 5, md: 7 } }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <SubtleCard sx={{ height: '100%' }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 900 }}>
                    Debate & Policy
                  </Typography>
                  <Typography sx={{ mt: 1, color: 'text.secondary' }}>
                    Learn how European politics works and practise public speaking in a safe environment.
                  </Typography>
                  <Button
                    variant="text"
                    sx={{ mt: 1.5, textTransform: 'none', fontWeight: 800 }}
                    onClick={() => navigate('/ueaustria')}
                  >
                    Understanding Europe
                  </Button>
                </CardContent>
              </SubtleCard>
            </Grid>

            <Grid item xs={12} md={4}>
              <SubtleCard sx={{ height: '100%' }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 900 }}>
                    Community
                  </Typography>
                  <Typography sx={{ mt: 1, color: 'text.secondary' }}>
                    Find friends across Europe, work in teams, and grow through shared experiences.
                  </Typography>
                  <Button
                    variant="text"
                    sx={{ mt: 1.5, textTransform: 'none', fontWeight: 800 }}
                    onClick={() => navigate('/membership')}
                  >
                    Join EYP Austria
                  </Button>
                </CardContent>
              </SubtleCard>
            </Grid>

            <Grid item xs={12} md={4}>
              <SubtleCard sx={{ height: '100%' }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 900 }}>
                    Opportunities
                  </Typography>
                  <Typography sx={{ mt: 1, color: 'text.secondary' }}>
                    Apply as a delegate, join organising teams, or support sessions — there’s a role for you.
                  </Typography>
                  <Button
                    variant="text"
                    sx={{ mt: 1.5, textTransform: 'none', fontWeight: 800 }}
                    onClick={() => navigate('/events#calls')}
                  >
                    View open calls
                  </Button>
                </CardContent>
              </SubtleCard>
            </Grid>
          </Grid>
        </Box>

       <Divider sx={{ my: { xs: 6, md: 8 } }} />

{/* SECTION BAND: Next Event + CTA (no borders, more “layout”) */}
<Box
  sx={{
    borderRadius: 6,
    px: { xs: 2.5, md: 5 },
    py: { xs: 4, md: 5 },
    background: `linear-gradient(135deg,
      ${alpha(theme.palette.primary.main, 0.10)} 0%,
      ${alpha(theme.palette.background.default, 1)} 55%)`,
  }}
>
  <Grid container spacing={3} alignItems="center">
    <Grid item xs={12} md={5}>
      <Typography variant="overline" sx={{ fontWeight: 900, letterSpacing: 1 }}>
        Next event
      </Typography>

      {nextEventLoading ? (
        <Box sx={{ mt: 1.5 }}>
          <Skeleton height={30} />
          <Skeleton height={22} width="70%" />
          <Skeleton height={22} width="55%" />
        </Box>
      ) : nextEvent ? (
        <Box sx={{ mt: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 950, letterSpacing: -0.4 }}>
            {nextEvent.title}
          </Typography>
          <Typography sx={{ mt: 0.8, color: "text.secondary" }}>
            {new Date(nextEvent.startDate.toDate()).toLocaleDateString("en-GB", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </Typography>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} sx={{ mt: 2.5 }}>
            <Button
              variant="contained"
              sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900 }}
              onClick={() => navigate("/events")}
            >
              View events
            </Button>
            <Button
              variant="outlined"
              sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900 }}
              onClick={() => navigate("/events#calls")}
            >
              Open calls
            </Button>
          </Stack>
        </Box>
      ) : (
        <Typography sx={{ mt: 1.5, color: "text.secondary" }}>
          No upcoming events found.
        </Typography>
      )}
    </Grid>

    <Grid item xs={12} md={7}>
      <Typography variant="h4" sx={{ fontWeight: 950, letterSpacing: -0.5 }}>
        Ready to join?
      </Typography>
      <Typography sx={{ mt: 1, color: "text.secondary", maxWidth: 720 }}>
        Check open calls, upcoming events, or become a member — we’d love to have you.
      </Typography>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} sx={{ mt: 3 }}>
        <Button
          variant="contained"
          sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900 }}
          onClick={() => navigate("/membership")}
        >
          Membership
        </Button>
        <Button
          variant="text"
          sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900 }}
          onClick={() => navigate("/contact")}
        >
          Contact
        </Button>
      </Stack>
    </Grid>
  </Grid>
</Box>
{/* <Box sx={{ mt: { xs: 6, md: 7 } }}>
  <Grid container spacing={3} alignItems="stretch">
    <Grid item xs={12} md={6}>
      <Typography variant="h5" sx={{ fontWeight: 950 }}>
        Where we’re active
      </Typography>
      <Typography sx={{ mt: 1, color: "text.secondary", maxWidth: 520 }}>
        Sessions and activities across Austria — from regional workshops to national events.
      </Typography>

      <Stack spacing={1.2} sx={{ mt: 2.5 }}>
        {[
          { city: "Vienna", note: "trainings & meetings" },
          { city: "Graz", note: "regional sessions" },
          { city: "Linz", note: "school outreach" },
          { city: "Innsbruck", note: "partner events" },
        ].map((x) => (
          <Box
            key={x.city}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.2,
              py: 1.1,
            }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: 999,
                backgroundColor: alpha(theme.palette.primary.main, 0.9),
                boxShadow: `0 0 0 6px ${alpha(theme.palette.primary.main, 0.12)}`,
              }}
            />
            <Typography sx={{ fontWeight: 900 }}>{x.city}</Typography>
            <Typography sx={{ color: "text.secondary" }}>— {x.note}</Typography>
          </Box>
        ))}
      </Stack>

      <Button
        variant="text"
        sx={{ mt: 2, textTransform: "none", fontWeight: 900 }}
        onClick={() => navigate("/events")}
      >
        See upcoming events →
      </Button>
    </Grid>

    <Grid item xs={12} md={6}>
      <Box
        sx={{
          borderRadius: 6,
          height: "100%",
          minHeight: 260,
          p: { xs: 2.5, md: 3 },
          position: "relative",
          overflow: "hidden",
          background: `linear-gradient(135deg,
            ${alpha(theme.palette.primary.main, 0.12)} 0%,
            ${alpha(theme.palette.text.primary, 0.02)} 45%,
            ${alpha(theme.palette.background.default, 1)} 100%)`,
        }}
      >
        <Typography sx={{ fontWeight: 950, letterSpacing: -0.2 }}>
          Austria footprint
        </Typography>
        <Typography sx={{ mt: 0.8, color: "text.secondary" }}>
          A simple visual now — later we can power it from your database.
        </Typography>

        <Box
          sx={{
            position: "absolute",
            right: -40,
            bottom: -60,
            width: 420,
            height: 320,
            borderRadius: "60% 40% 55% 45% / 55% 45% 55% 45%",
            background: `radial-gradient(${alpha(theme.palette.primary.main, 0.35)} 1px, transparent 1px)`,
            backgroundSize: "14px 14px",
            opacity: 0.55,
            transform: "rotate(-8deg)",
          }}
        />

        <Box
          sx={{
            position: "absolute",
            left: -80,
            top: 60,
            width: 240,
            height: 240,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.20)} 0%, transparent 70%)`,
            filter: "blur(8px)",
          }}
        />
      </Box>
    </Grid>
  </Grid>
</Box> */}

{/* Partners: simple strip, no border container */}
<Box sx={{ mt: { xs: 6, md: 7 }, textAlign: "center" }}>
  <Typography variant="h5" sx={{ fontWeight: 950 }}>
    Our partners
  </Typography>
  <Typography sx={{ mt: 1, color: "text.secondary" }}>
    We’re grateful for the support that makes our work possible.
  </Typography>

  <Stack
    direction="row"
    spacing={{ xs: 2, md: 4 }}
    useFlexGap
    flexWrap="wrap"
    justifyContent="center"
    alignItems="center"
    sx={{ mt: 3 }}
  >
    {[partnerLogo1, partnerLogo2, partnerLogo3, partnerLogo4].map((src, idx) => (
      <PartnerLogo key={idx} src={src} alt={`Partner ${idx + 1}`} />
    ))}
  </Stack>

  <Button
    variant="outlined"
    onClick={() => navigate("/partner")}
    sx={{ mt: 3, borderRadius: 999, textTransform: "none", fontWeight: 900 }}
  >
    View all partners
  </Button>
</Box>

{/* Updates: clean list + Instagram */}
<Box sx={{ mt: { xs: 6, md: 8 }, pb: 6 }}>
  <Typography variant={isMobile ? "h4" : "h3"} sx={{ fontWeight: 950, letterSpacing: -0.6 }}>
    Latest updates
  </Typography>
  <Typography sx={{ mt: 1, color: "text.secondary", maxWidth: 720 }}>
    News from EYP Austria and a quick social highlight.
  </Typography>

  <Grid container spacing={4} sx={{ mt: 0.5 }}>
    {/* NEWS LIST (not cards) */}
    <Grid item xs={12} md={7}>
      <Stack spacing={2}>
        {newsLoading ? (
          <>
            <Skeleton height={34} />
            <Skeleton height={90} />
            <Skeleton height={34} />
            <Skeleton height={90} />
          </>
        ) : (
          news.map((n) => {
            const title = n?.title ?? "News";
            const dateStr =
              n?.timestamp?.toDate ? new Date(n.timestamp.toDate()).toDateString() : null;

            // try common field names so it works even if your DB differs
            const rawText = n?.content ?? n?.text ?? n?.body ?? "";
            const preview =
              rawText.length > 220 ? `${rawText.slice(0, 150)}…` : rawText || "Open to read more.";

            return (
              <Box
                key={n.id}
                onClick={() => navigate("/news")}
                sx={{
                  cursor: "pointer",
                  p: { xs: 2, md: 2.5 },
                  borderLeft: `4px solid ${alpha(theme.palette.primary.main, 0.55)}`,
                  borderRadius: 2.5,
                  backgroundColor: alpha(theme.palette.text.primary, 0.03),
                  transition: "transform .2s ease, background-color .2s ease",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    backgroundColor: alpha(theme.palette.text.primary, 0.05),
                  },
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 950 }}>
                  {title}
                </Typography>

                {dateStr && (
                  <Typography variant="body2" sx={{ mt: 0.4, color: "text.secondary" }}>
                    {dateStr}
                  </Typography>
                )}

                <Typography sx={{ mt: 1.2, color: "text.secondary" }}>
                  {excerpt(n.content, 150)}
                </Typography>
              </Box>
            );
          })

        )}

        <Button
          variant="text"
          sx={{ alignSelf: "flex-start", textTransform: "none", fontWeight: 900 }}
          onClick={() => navigate("/news")}
        >
          More news →
        </Button>
      </Stack>
    </Grid>

    {/* INSTAGRAM */}
    <Grid item xs={12} md={5}>
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        {consent.thirdParty ? (
          <InstagramEmbed
            url="https://www.instagram.com/reel/C5VVwxxxSK8/?utm_source=ig_web_copy_link&igshid=MzRlODBiNWFlZA=="
            width="100%"
            maxWidth={420}
            hideCaption={false}
          />

        ) : (
          <Box
            sx={{
              width: "100%",
              maxWidth: 420,
              height: 420,
              backgroundColor: alpha(theme.palette.text.primary, 0.04),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              borderRadius: 2,
              border: `2px dashed ${alpha(theme.palette.text.primary, 0.2)}`,
              px: 2,
              textAlign: "center",
            }}
          >
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              Instagram content is disabled.
            </Typography>
            <Button
              variant="contained"
              onClick={openConsentDialog}
              sx={{ textTransform: "none", borderRadius: 999, fontWeight: 900 }}
            >
              Change cookie settings to view
            </Button>
          </Box>
        )}
      </Box>
    </Grid>
  </Grid>
</Box>

</Container>
</Box>
);}

export default HomePage;
